
import React from 'react';
import { Truck, BrainCircuit, Boxes, Zap } from 'lucide-react';
import { SystemConfig, LogisticsProfile, DeliveryProvider } from '../../types';
import { SmartNumberInput as SmartInput } from '../SmartNumberInput';
import { AngleSelector } from '../AngleSelector';
import { ProviderSettings } from '../ProviderSettings';
import { DECISION_POLICY_PROFILES, DEFAULT_DECISION_POLICY_PROFILE } from '../../constants';

interface Props {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
  providers: DeliveryProvider[];
  setProviders: (providers: DeliveryProvider[]) => void;
}

export const SettingsTabThirdParty: React.FC<Props> = ({ config, setConfig, providers, setProviders }) => {
  const activeDecisionPolicyId = config.oracle?.decisionPolicyProfile ?? DEFAULT_DECISION_POLICY_PROFILE;
  const activeDecisionPolicy = DECISION_POLICY_PROFILES[activeDecisionPolicyId] ?? DECISION_POLICY_PROFILES[DEFAULT_DECISION_POLICY_PROFILE];

  const updateThirdPartyConfig = (key: keyof LogisticsProfile, value: number) => {
    setConfig({ ...config, thirdPartyConfig: { ...config.thirdPartyConfig, [key]: value } });
  };

  const SectionHeader = ({ icon: Icon, title, subTitle, color = 'text-sal-cyber-teal' }: any) => (
    <div className="flex items-center gap-3 border-b border-border-dim pb-4 mb-6">
      <div className={`p-2.5 bg-card-bg border border-border-dim rounded-xl ${color}`}><Icon className="w-5 h-5" /></div>
      <div><h4 className="text-base font-black text-text-primary uppercase tracking-widest">{title}</h4><p className="text-xs text-text-muted font-bold uppercase tracking-tighter">{subTitle}</p></div>
    </div>
  );

  const LogisticsParamsInputs = ({ profile, onUpdate, label }: { profile: LogisticsProfile, onUpdate: (k: keyof LogisticsProfile, v: number) => void, label: string }) => (
    <div className="bg-card-bg border border-border-dim rounded-3xl p-6 shadow-xl relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1.5 h-full bg-status-pending/50"></div>
      <SectionHeader icon={BrainCircuit} title={label} subTitle="Define como o sistema agrupa e otimiza rotas para entregadores externos." color="text-status-pending" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AngleSelector
          value={profile.clusteringMaxAngleDegrees ?? 50}
          onChange={(val) => onUpdate('clusteringMaxAngleDegrees', val)}
          color="#f97316" min={10} max={180}
          description="Ângulo máximo em graus para agrupamento em parceiros. Menor = mais direto."
        />
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SmartInput
            label="Zig-Zag (Tempo)"
            value={profile.zigzagIgnoreTimeMinutes ?? 3}
            onChange={(v) => onUpdate('zigzagIgnoreTimeMinutes', v)}
            description="Ignorar ângulo em viagens rápidas (< min)."
          />
          <SmartInput
            label="Zig-Zag (Distância)"
            value={profile.clusteringMinDistanceForAngleCheck ?? 1.5}
            onChange={(v) => onUpdate('clusteringMinDistanceForAngleCheck', v)}
            description="Raio físico para regra Zig-Zag (km)."
          />
          <SmartInput
            label="Raio de Agrupamento"
            value={profile.clusteringMaxRadiusKm ?? 10}
            onChange={(v) => onUpdate('clusteringMaxRadiusKm', v)}
            description="Distância máxima entre pedidos para agrupamento (km)."
          />
          <SmartInput
            label="Tempo Atendimento"
            value={profile.serviceTimeMinutes || 4}
            onChange={(v) => onUpdate('serviceTimeMinutes', v)}
            description="Tempo médio de atendimento no cliente (min)."
          />
          <SmartInput
            label="Margem SLA"
            value={profile.slaToleranceMinutes ?? 7}
            onChange={(v) => onUpdate('slaToleranceMinutes', v)}
            description="Margem de tolerância para agrupamento (min)."
          />
          <SmartInput
            label="Limite por Rota"
            value={profile.maxOrdersPerRoute ?? 2}
            onChange={(v) => onUpdate('maxOrdersPerRoute', v)}
            description="Número máximo de pedidos por rota/viagem."
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300 pb-28">
      <div>
        <h3 className="text-3xl font-black text-text-primary mb-1 uppercase tracking-tighter">Parceiros e Terceirização</h3>
        <p className="text-sm text-text-muted font-medium">Configure as tabelas de preço, lógicas de despacho externo e regras para a decisão de terceirização.</p>
      </div>

      <LogisticsParamsInputs label="Lógica de Otimização de Rotas [Apps Parceiros]" profile={config.thirdPartyConfig} onUpdate={updateThirdPartyConfig} />

      <section className="bg-card-bg border border-border-dim rounded-3xl p-6 shadow-xl relative overflow-hidden">
        <SectionHeader icon={Boxes} title="Regras de Arbitragem" subTitle="Gatilhos e critérios para a decisão de terceirização de pedidos." color="text-status-info" />
        <div className="mb-4 p-3 rounded-2xl border border-border-dim bg-bg-main/40">
          <p className="text-xs font-black text-text-primary uppercase tracking-widest">Política Ativa: {activeDecisionPolicy.label}</p>
          <p className="text-xs text-text-muted font-medium mt-1">{activeDecisionPolicy.description}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 relative z-10">
          <div className="bg-bg-main/50 p-4 rounded-2xl border border-border-dim h-full flex flex-col space-y-1">
            <label className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest block">Desvio Máximo de Custo</label>
            <SmartInput value={config.thirdPartyDeviationThreshold ?? 1.4} onChange={(v) => setConfig({ ...config, thirdPartyDeviationThreshold: v })} step={0.1} min={1.0} description="Fator de desvio aceitável para frota própria." />
          </div>
          <div className="bg-bg-main/50 p-4 rounded-2xl border border-border-dim h-full flex flex-col space-y-1">
            <label className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest block">Desc. Rota Inteligente</label>
            <SmartInput value={config.thirdPartySmartRouteDiscount ?? 15} onChange={(v) => setConfig({ ...config, thirdPartySmartRouteDiscount: v })} step={1} min={0} max={100} description="% de economia em agrupamentos." />
          </div>
          <div className="bg-bg-main/50 p-4 rounded-2xl border border-border-dim h-full flex flex-col space-y-1">
            <label className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest block">Economia Mínima</label>
            <SmartInput value={config.thirdPartyMinSavings ?? 0.50} onChange={(v) => setConfig({ ...config, thirdPartyMinSavings: v })} isCurrency step={0.10} min={0} description="Valor mínimo de economia (R$)." />
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div className="flex items-center justify-between px-2"><h3 className="text-sm font-black text-text-muted uppercase tracking-[0.2em] flex items-center gap-2"><Truck className="w-4 h-4" /> Tabelas de Preço Ativas</h3></div>
        <div className="bg-bg-main/30 p-2 rounded-3xl border border-border-dim shadow-inner"><ProviderSettings providers={providers} setProviders={setProviders} config={config} /></div>
      </section>
    </div>
  );
};
